import React, { useState } from "react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Download, FileText, FileSpreadsheet, FileImage, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable Export dropdown (CSV / Excel / PDF).
 *
 * Props:
 *   - endpoint: API path that accepts `fmt` query param (e.g. "/billing/export")
 *   - params:   extra query-params object (client_id, branch_id, month, fy, …)
 *   - filename: base filename (no extension) — server sets the correct suffix
 *   - label?:   button label (default "Export")
 *   - testid?:  data-testid root
 */
export default function ExportMenu({ endpoint, params = {}, filename = "export", label = "Export", testid = "export" }) {
  const [busy, setBusy] = useState(false);

  const fetchAndDownload = async (fmt) => {
    setBusy(true);
    try {
      const qs = new URLSearchParams();
      qs.set("fmt", fmt);
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") qs.set(k, v);
      });
      const t = localStorage.getItem("mptg_token");
      const res = await fetch(`${API}${endpoint}?${qs.toString()}`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const ext = fmt === "xlsx" ? "xlsx" : fmt === "pdf" ? "pdf" : "csv";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ext.toUpperCase()}`);
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="border-slate-200" disabled={busy} data-testid={`${testid}-btn`}>
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => fetchAndDownload("csv")} data-testid={`${testid}-csv`}>
          <FileText className="w-4 h-4 mr-2" /> CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => fetchAndDownload("xlsx")} data-testid={`${testid}-xlsx`}>
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => fetchAndDownload("pdf")} data-testid={`${testid}-pdf`}>
          <FileImage className="w-4 h-4 mr-2" /> PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
